// Appendix A's dictionary, ported verbatim, plus a couple of new keys this rewrite needs
// that didn't exist before (marked below) — every UI string goes through here, no hardcoded
// chrome text anywhere else in the widget (that was the old app's bug: "Katso lisää" leaking
// into en/sv requests).
//
// AI-PT content (aipt* keys, GOAL/LEVEL/FREQUENCY option labels, LOADER_MESSAGES): the old app
// had ZERO Swedish anywhere in this flow (Appendix C), and didn't give exact copy for the
// access-denied/unavailable/PDF-link/thumbs strings in any language. All Swedish text below,
// plus the missing English/Finnish strings, is newly authored for this rewrite, not ported —
// flagging it as machine-translated and not reviewed by a native speaker, worth a real
// translation pass before this reaches production.
export type Locale = "fi" | "en" | "sv";

export const WEEKDAY_ORDER = [
  "mon",
  "tue",
  "wed",
  "thu",
  "fri",
  "sat",
  "sun",
] as const;
export const GOAL_OPTIONS = [
  "weight_loss",
  "muscle_gain",
  "general_fitness",
  "endurance",
] as const;
export const LEVEL_OPTIONS = ["beginner", "intermediate", "advanced"] as const;
export const FREQUENCY_OPTIONS = ["1", "2", "3", "4"] as const;

export const STRINGS: Record<Locale, Record<string, string>> = {
  en: {
    now: "Visitors now",
    week: "Weekly visitors",
    aipt: "Create personalized workout plan",
    aiptShort: "Create workout plan",
    close: "Close",
    help: "What can I do here?",
    hintTabs: "Switch between right now and the weekly rhythm",
    hintNow: "People at the gym right now",
    hintForecast: "Forecast for the next hours: green is quiet, red is busy",
    hintWeek: "Pick a day and point at the curve to see how an hour splits",
    hintAipt: "AI builds you a workout plan for this gym",
    maxIsPeak: "Max is the gym's busiest hour in a typical week",
    maxIsCapacity: "Max is the gym's estimated capacity",
    personsShort: "people",
    viewForecast: "View the forecast for the next hours",
    availabilityForNextHours: "Visitors in the next hours",
    mon: "mon",
    tue: "tue",
    wed: "wed",
    thu: "thu",
    fri: "fri",
    sat: "sat",
    sun: "sun",
    people: "People",
    currentlyPeople: "Currently training {n} people",
    poweredBy: "GymPlus",
    privacy: "Privacy notice",
    closedLabel: "Gym is closed, opens at",
    loading: "Loading",
    loadError: "Unable to load availability.",
    loadErrorNote:
      "The gym's data is not responding right now. You can try again.",
    retry: "Try again",
    // New — no equivalent existed in the old app (Phase 5's chain switcher was built once,
    // then commented out, with no finished copy).
    otherLocations: "Other locations",
    // New — the data-type="onlytotal" compact view's link to the full widget modal.
    seeMore: "See more",

    // AI-PT (Phase 8) — question bank text is Appendix C's verbatim fi/en; options/loader/
    // answer copy not given exact text there is authored fresh (see file header).
    aiptDisclaimer:
      "Disclaimer: Gym training can lead to injuries. GymPlus or the website owner assumes no liability.",
    aiptDescription: "Let AI create your workout program!",
    aiptSubmit: "Create my workout plan",
    aiptGoalQuestion: "What is your goal?",
    "aiptGoal.weight_loss": "Weight loss",
    "aiptGoal.muscle_gain": "Muscle gain",
    "aiptGoal.general_fitness": "General fitness",
    "aiptGoal.endurance": "Endurance",
    aiptLevelQuestion: "What is your level?",
    "aiptLevel.beginner": "Beginner",
    "aiptLevel.intermediate": "Intermediate",
    "aiptLevel.advanced": "Advanced",
    aiptFrequencyQuestion: "How often to gym (weekly)?",
    "aiptFrequency.1": "1 day",
    "aiptFrequency.2": "2 days",
    "aiptFrequency.3": "3 days",
    "aiptFrequency.4": "4 days",
    "aiptFrequency.5": "5 days",
    "aiptFrequency.6": "6 days",
    "aiptFrequency.7": "7 days",
    aiptLoaderHeader: "Working...",
    aiptAnswerTitle: "Your Workout Program",
    aiptErrorGeneric: "Error: Failed to generate workout program.",
    aiptErrorAccessDenied:
      "Access denied. Please check your subscription or contact support.",
    aiptErrorUnavailable:
      "The service is temporarily unavailable. Please try again later.",
    aiptDownloadPdf: "Download PDF",
    aiptThumbsUp: "Helpful",
    aiptThumbsDown: "Not helpful",
    aiptFeedbackThanks: "Thanks for the feedback!",
    aiptEmailPrompt: "I would like to hear more about the personal trainer services this gym offers",
    aiptEmailPlaceholder: "you@example.com",
    aiptEmailSend: "Send",
    aiptEmailConsent: "I agree the gym can contact me about this",
    aiptEmailConsentNote: "Only this gym will see your details.",
    aiptEmailSent: "Sent — the gym will be in touch with you.",
    aiptEmailError: "Could not send your request. Please try again later.",
  },
  fi: {
    now: "Kävijät nyt",
    week: "Viikon kävijät",
    aipt: "Luo henkilökohtainen treeniohjelma",
    aiptShort: "Luo treeniohjelma",
    close: "Sulje",
    help: "Mitä tässä voi tehdä?",
    hintTabs: "Vaihda: kävijät juuri nyt tai koko viikon rytmi",
    hintNow: "Kävijöitä salilla tällä hetkellä",
    hintForecast: "Lähituntien ennuste: vihreä on väljää, punainen täyttä",
    hintWeek: "Valitse päivä ja osoita käyrää, näet tunnin jakauman",
    hintAipt: "Tekoäly laatii sinulle treeniohjelman tälle salille",
    maxIsPeak: "Max on salin ruuhkaisin tunti tavallisella viikolla",
    maxIsCapacity: "Max on salin arvioitu kapasiteetti",
    personsShort: "hlöä",
    viewForecast: "Katso ennuste seuraaville tunneille",
    availabilityForNextHours: "Kävijöitä lähitunteina",
    mon: "ma",
    tue: "ti",
    wed: "ke",
    thu: "to",
    fri: "pe",
    sat: "la",
    sun: "su",
    people: "Kävijää",
    currentlyPeople: "Kuntosalilla {n} ihmistä",
    poweredBy: "GymPlus",
    privacy: "Tietosuojaseloste",
    closedLabel: "Sali on suljettu, avataan klo",
    loading: "Ladataan",
    loadError: "Tietojen lataaminen epäonnistui.",
    loadErrorNote:
      "Yhteys salin tietoihin ei juuri nyt vastaa. Voit yrittää uudelleen.",
    retry: "Yritä uudelleen",
    otherLocations: "Muut toimipisteet",
    seeMore: "Katso lisää",

    aiptDisclaimer:
      "Vastuuvapauslauseke: Kuntosaliharjoittelu voi johtaa loukkaantumisiin. GymPlus tai sivuston omistaja ei ota niistä vastuuta.",
    aiptDescription: "Anna tekoälyn luoda sinulle harjoitusohjelma!",
    aiptSubmit: "Luo treeniohjelmani",
    aiptGoalQuestion: "Tavoitteesi saliharjoittelulle?",
    "aiptGoal.weight_loss": "Painonpudotus",
    "aiptGoal.muscle_gain": "Lihasmassa",
    "aiptGoal.general_fitness": "Yleiskunto",
    "aiptGoal.endurance": "Kestävyys",
    aiptLevelQuestion: "Tämänhetkinen tasosi?",
    "aiptLevel.beginner": "Aloittelija",
    "aiptLevel.intermediate": "Keskitaso",
    "aiptLevel.advanced": "Edistynyt",
    aiptFrequencyQuestion: "Kuinka usein viikossa salille?",
    "aiptFrequency.1": "1 päivä",
    "aiptFrequency.2": "2 päivää",
    "aiptFrequency.3": "3 päivää",
    "aiptFrequency.4": "4 päivää",
    "aiptFrequency.5": "5 päivää",
    "aiptFrequency.6": "6 päivää",
    "aiptFrequency.7": "7 päivää",
    aiptLoaderHeader: "Työstetään...",
    aiptAnswerTitle: "Harjoitusohjelmasi",
    aiptErrorGeneric: "Virhe: Harjoitusohjelman luominen epäonnistui.",
    aiptErrorAccessDenied:
      "Pääsy evätty. Tarkista tilauksesi tai ota yhteyttä ylläpitoon.",
    aiptErrorUnavailable:
      "Palvelu ei ole juuri nyt käytettävissä. Yritä myöhemmin uudelleen.",
    aiptDownloadPdf: "Lataa PDF",
    aiptThumbsUp: "Hyödyllinen",
    aiptThumbsDown: "Ei hyödyllinen",
    aiptFeedbackThanks: "Kiitos palautteesta!",
    aiptEmailPrompt: "Haluaisin kuulla lisää salin personal trainer -palveluista",
    aiptEmailPlaceholder: "nimi@esimerkki.fi",
    aiptEmailSend: "Lähetä",
    aiptEmailConsent: "Sali saa olla minuun yhteydessä tästä",
    aiptEmailConsentNote: "Vain tämä sali näkee tietosi.",
    aiptEmailSent: "Lähetetty — sali on sinuun yhteydessä.",
    aiptEmailError:
      "Lähetys ei onnistunut. Yritä myöhemmin uudelleen.",
  },
  sv: {
    now: "Besökare nu",
    week: "Besökare i veckan",
    aipt: "Skapa ett personligt träningsprogram",
    aiptShort: "Skapa träningsprogram",
    close: "Stäng",
    help: "Vad kan jag göra här?",
    hintTabs: "Växla mellan just nu och veckans rytm",
    hintNow: "Besökare på gymmet just nu",
    hintForecast:
      "Prognos för de närmaste timmarna: grönt är lugnt, rött är fullt",
    hintWeek: "Välj en dag och peka på kurvan för att se timmens fördelning",
    hintAipt: "AI gör ett träningsprogram åt dig för det här gymmet",
    maxIsPeak: "Max är gymmets mest besökta timme en vanlig vecka",
    maxIsCapacity: "Max är gymmets uppskattade kapacitet",
    personsShort: "pers.",
    viewForecast: "Visa prognosen för de kommande timmarna",
    availabilityForNextHours: "Besökare de närmaste timmarna",
    mon: "må",
    tue: "ti",
    wed: "on",
    thu: "to",
    fri: "fr",
    sat: "lö",
    sun: "sö",
    people: "Besökare",
    currentlyPeople: "För närvarande {n} personer på gymmet",
    poweredBy: "GymPlus",
    privacy: "Integritetsmeddelande",
    closedLabel: "Gymmet är stängt, öppnar",
    loading: "Laddar",
    loadError: "Det gick inte att läsa in tillgänglighet.",
    loadErrorNote: "Gymmets data svarar inte just nu. Du kan försöka igen.",
    retry: "Försök igen",
    otherLocations: "Andra platser",
    seeMore: "Visa mer",

    aiptDisclaimer:
      "Ansvarsfriskrivning: Gymträning kan leda till skador. GymPlus eller webbplatsens ägare tar inget ansvar för detta.",
    aiptDescription: "Låt AI skapa ett träningsprogram åt dig!",
    aiptSubmit: "Skapa mitt träningsprogram",
    aiptGoalQuestion: "Vad är ditt mål?",
    "aiptGoal.weight_loss": "Viktminskning",
    "aiptGoal.muscle_gain": "Muskeltillväxt",
    "aiptGoal.general_fitness": "Allmän kondition",
    "aiptGoal.endurance": "Uthållighet",
    aiptLevelQuestion: "Vad är din nivå?",
    "aiptLevel.beginner": "Nybörjare",
    "aiptLevel.intermediate": "Medelnivå",
    "aiptLevel.advanced": "Avancerad",
    aiptFrequencyQuestion: "Hur ofta till gymmet (per vecka)?",
    "aiptFrequency.1": "1 dag",
    "aiptFrequency.2": "2 dagar",
    "aiptFrequency.3": "3 dagar",
    "aiptFrequency.4": "4 dagar",
    "aiptFrequency.5": "5 dagar",
    "aiptFrequency.6": "6 dagar",
    "aiptFrequency.7": "7 dagar",
    aiptLoaderHeader: "Arbetar...",
    aiptAnswerTitle: "Ditt träningsprogram",
    aiptErrorGeneric: "Fel: Det gick inte att skapa träningsprogrammet.",
    aiptErrorAccessDenied:
      "Åtkomst nekad. Kontrollera din prenumeration eller kontakta support.",
    aiptErrorUnavailable:
      "Tjänsten är tillfälligt otillgänglig. Försök igen senare.",
    aiptDownloadPdf: "Ladda ner PDF",
    aiptThumbsUp: "Hjälpsam",
    aiptThumbsDown: "Inte hjälpsam",
    aiptFeedbackThanks: "Tack för din feedback!",
    aiptEmailPrompt: "Jag vill gärna höra mer om gymmets personliga tränartjänster",
    aiptEmailPlaceholder: "namn@exempel.se",
    aiptEmailSend: "Skicka",
    aiptEmailConsent: "Gymmet får kontakta mig om detta",
    aiptEmailConsentNote: "Endast detta gym ser dina uppgifter.",
    aiptEmailSent: "Skickat — gymmet kontaktar dig snart.",
    aiptEmailError: "Det gick inte att skicka. Försök igen senare.",
  },
};

/** 9 rotating loader messages (Appendix C), cycled every 4s while waiting for the AI Lambda.
 * English text is Appendix C's verbatim list; fi/sv are newly authored (see file header). */
export const LOADER_MESSAGES: Record<Locale, string[]> = {
  en: [
    "This might take a few minutes...",
    "Understanding your goals...",
    "Collecting data...",
    "Analyzing your preferences...",
    "Evaluating equipment availability..",
    "Designing your workout program...",
    "Optimizing for your schedule...",
    "Finalizing your workout plan...",
    "Almost there...",
  ],
  fi: [
    "Tämä saattaa kestää muutaman minuutin...",
    "Tulkitaan tavoitteitasi...",
    "Kerätään tietoja...",
    "Analysoidaan mieltymyksiäsi...",
    "Arvioidaan laitteiden saatavuutta..",
    "Suunnitellaan harjoitusohjelmaasi...",
    "Optimoidaan aikataulusi mukaan...",
    "Viimeistellään harjoitussuunnitelmaasi...",
    "Melkein valmista...",
  ],
  sv: [
    "Det här kan ta några minuter...",
    "Tolkar dina mål...",
    "Samlar in data...",
    "Analyserar dina preferenser...",
    "Utvärderar tillgången på utrustning..",
    "Utformar ditt träningsprogram...",
    "Optimerar utifrån ditt schema...",
    "Färdigställer din träningsplan...",
    "Nästan klart...",
  ],
};

/** Falls back to the key itself if missing — same silent-fallback the old app had (Appendix
 * A says not to port it), but a build-time check isn't practical for hand-authored copy here,
 * so this is the one place that fallback still exists, worth revisiting with a dictionary test. */
export function t(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const template = STRINGS[locale]?.[key] ?? STRINGS.fi[key] ?? key;
  if (!vars) return template;
  return Object.entries(vars).reduce(
    (text, [name, value]) => text.replace(`{${name}}`, String(value)),
    template,
  );
}
